import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import api from '../../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { 
  School, 
  Plus, 
  Search,
  Settings,
  Trash2,
  Loader2,
  Users,
  Ban,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';

const AdminSchoolsPage = () => {
  const navigate = useNavigate();
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [newSchool, setNewSchool] = useState({
    name: '',
    address: '',
    contact_person: '',
    contact_phone: '',
    contact_email: '',
    veracross_school_route: '',
    veracross_client_id: '',
    veracross_client_secret: ''
  });

  useEffect(() => {
    fetchSchools();
  }, []);

  const fetchSchools = async () => {
    try {
      const response = await api.get('/schools');
      setSchools(response.data);
    } catch (error) {
      toast.error('Failed to fetch schools');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSchool = async () => {
    if (!newSchool.name || !newSchool.contact_email) {
      toast.error('Please fill in school name and admin email');
      return;
    }
    if (!newSchool.veracross_school_route || !newSchool.veracross_client_id || !newSchool.veracross_client_secret) {
      toast.error('Please fill in all Veracross configuration fields');
      return;
    }

    setFormLoading(true);
    try {
      await api.post('/schools', newSchool);
      toast.success('School created successfully');
      setCreateDialogOpen(false);
      setNewSchool({
        name: '',
        address: '',
        contact_person: '',
        contact_phone: '',
        contact_email: '',
        veracross_school_route: '',
        veracross_client_id: '',
        veracross_client_secret: ''
      });
      fetchSchools();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create school');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteSchool = async () => {
    if (!selectedSchool) return;
    
    setFormLoading(true);
    try {
      await api.delete(`/schools/${selectedSchool.id}`);
      toast.success('School and all related data deleted');
      setDeleteDialogOpen(false);
      setSelectedSchool(null);
      fetchSchools();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete school');
    } finally {
      setFormLoading(false);
    }
  };

  const handleSuspendToggle = async () => {
    if (!selectedSchool) return;
    
    setFormLoading(true);
    try {
      const endpoint = selectedSchool.is_suspended 
        ? `/schools/${selectedSchool.id}/unsuspend`
        : `/schools/${selectedSchool.id}/suspend`;
      
      await api.post(endpoint);
      toast.success(selectedSchool.is_suspended ? 'School unsuspended' : 'School suspended');
      setSuspendDialogOpen(false);
      setSelectedSchool(null);
      fetchSchools();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update school status');
    } finally {
      setFormLoading(false);
    }
  };

  const filteredSchools = schools.filter(s => 
    s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.contact_email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-schools-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-outfit text-3xl font-bold text-slate-900">Schools</h1>
            <p className="text-slate-500 mt-1">Manage school accounts and configurations</p>
          </div>
          <Button
            className="bg-indigo-600 hover:bg-indigo-700"
            onClick={() => setCreateDialogOpen(true)}
            data-testid="create-school-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add School
          </Button>
        </div>

        {/* Search */}
        <Card>
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search schools..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-schools-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Schools Table */}
        <Card>
          <CardHeader>
            <CardTitle className="font-outfit">All Schools</CardTitle>
            <CardDescription>{filteredSchools.length} school(s) found</CardDescription>
          </CardHeader>
          <CardContent>
            {filteredSchools.length === 0 ? (
              <div className="text-center py-12">
                <School className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No schools found</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>School Name</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Contact Person</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Veracross Route</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSchools.map((school) => (
                    <TableRow 
                      key={school.id} 
                      data-testid={`school-row-${school.id}`}
                      className={school.is_suspended ? 'bg-rose-50/50' : ''}
                    >
                      <TableCell className="font-medium">{school.name}</TableCell>
                      <TableCell>
                        {school.is_suspended ? (
                          <Badge variant="destructive" className="flex items-center gap-1 w-fit">
                            <Ban className="h-3 w-3" />
                            Suspended
                          </Badge>
                        ) : (
                          <Badge className="bg-emerald-100 text-emerald-700 flex items-center gap-1 w-fit">
                            <CheckCircle className="h-3 w-3" />
                            Active
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>{school.contact_person}</TableCell>
                      <TableCell>{school.contact_email}</TableCell>
                      <TableCell>
                        <code className="px-2 py-1 bg-slate-100 rounded text-sm">
                          {school.veracross_school_route || '-'}
                        </code>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/admin/schools/${school.id}/users`)}
                            data-testid={`view-users-${school.id}`}
                            title="View Users"
                          >
                            <Users className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/admin/schools/${school.id}/config`)}
                            data-testid={`config-school-${school.id}`}
                            title="Configure School"
                          >
                            <Settings className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className={school.is_suspended ? 'text-emerald-600 hover:text-emerald-700' : 'text-amber-600 hover:text-amber-700'}
                            onClick={() => {
                              setSelectedSchool(school);
                              setSuspendDialogOpen(true);
                            }}
                            data-testid={`suspend-school-${school.id}`}
                            title={school.is_suspended ? 'Unsuspend School' : 'Suspend School'}
                          >
                            {school.is_suspended ? <CheckCircle className="h-4 w-4" /> : <Ban className="h-4 w-4" />}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-rose-600 hover:text-rose-700"
                            onClick={() => {
                              setSelectedSchool(school);
                              setDeleteDialogOpen(true);
                            }}
                            data-testid={`delete-school-${school.id}`}
                            title="Delete School"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Create School Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-outfit">Add New School</DialogTitle>
              <DialogDescription>
                Create a new school account. An admin user will be created automatically.
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">School Name *</Label>
                <Input
                  id="name"
                  value={newSchool.name}
                  onChange={(e) => setNewSchool({ ...newSchool, name: e.target.value })}
                  placeholder="Westside Academy"
                  data-testid="school-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  value={newSchool.address}
                  onChange={(e) => setNewSchool({ ...newSchool, address: e.target.value })}
                  placeholder="123 School St, City, ST"
                  data-testid="school-address-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact_person">Contact Person</Label>
                <Input
                  id="contact_person"
                  value={newSchool.contact_person}
                  onChange={(e) => setNewSchool({ ...newSchool, contact_person: e.target.value })}
                  placeholder="John Smith"
                  data-testid="school-contact-person-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact_phone">Contact Phone</Label>
                <Input
                  id="contact_phone"
                  value={newSchool.contact_phone}
                  onChange={(e) => setNewSchool({ ...newSchool, contact_phone: e.target.value })}
                  placeholder="(555) 123-4567"
                  data-testid="school-contact-phone-input"
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label htmlFor="contact_email">Admin Email *</Label>
                <Input
                  id="contact_email"
                  type="email"
                  value={newSchool.contact_email}
                  onChange={(e) => setNewSchool({ ...newSchool, contact_email: e.target.value })}
                  placeholder="admin@school.edu"
                  data-testid="school-contact-email-input"
                />
              </div>
              <div className="col-span-2 border-t pt-4 mt-2">
                <h4 className="font-medium text-slate-900 mb-3">Veracross Configuration</h4>
              </div>
              <div className="space-y-2">
                <Label htmlFor="veracross_school_route">School Route</Label>
                <Input
                  id="veracross_school_route"
                  value={newSchool.veracross_school_route}
                  onChange={(e) => setNewSchool({ ...newSchool, veracross_school_route: e.target.value })}
                  placeholder="westside"
                  data-testid="veracross-route-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="veracross_client_id">Client ID</Label>
                <Input
                  id="veracross_client_id"
                  value={newSchool.veracross_client_id}
                  onChange={(e) => setNewSchool({ ...newSchool, veracross_client_id: e.target.value })}
                  placeholder="Enter client ID"
                  data-testid="veracross-client-id-input"
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label htmlFor="veracross_client_secret">Client Secret</Label>
                <Input
                  id="veracross_client_secret"
                  type="password"
                  value={newSchool.veracross_client_secret}
                  onChange={(e) => setNewSchool({ ...newSchool, veracross_client_secret: e.target.value })}
                  placeholder="Enter client secret"
                  data-testid="veracross-client-secret-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                className="bg-indigo-600 hover:bg-indigo-700"
                onClick={handleCreateSchool}
                disabled={formLoading}
                data-testid="submit-create-school-btn"
              >
                {formLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create School'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-rose-600 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Delete School
              </DialogTitle>
              <DialogDescription className="space-y-2">
                <p>Are you sure you want to delete <strong>{selectedSchool?.name}</strong>?</p>
                <p className="text-rose-600 font-medium">This will permanently delete:</p>
                <ul className="list-disc list-inside text-sm text-slate-600">
                  <li>All school users and their data</li>
                  <li>All teachers and conference URLs</li>
                  <li>All conference sets and schedules</li>
                  <li>All export files and configurations</li>
                </ul>
                <p className="text-rose-600 font-medium">This action cannot be undone.</p>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteSchool}
                disabled={formLoading}
                data-testid="confirm-delete-school-btn"
              >
                {formLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Delete School'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Suspend/Unsuspend Dialog */}
        <Dialog open={suspendDialogOpen} onOpenChange={setSuspendDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className={selectedSchool?.is_suspended ? 'text-emerald-600' : 'text-amber-600'}>
                {selectedSchool?.is_suspended ? 'Unsuspend School' : 'Suspend School'}
              </DialogTitle>
              <DialogDescription>
                {selectedSchool?.is_suspended ? (
                  <p>Are you sure you want to unsuspend <strong>{selectedSchool?.name}</strong>? Users will be able to login again.</p>
                ) : (
                  <>
                    <p>Are you sure you want to suspend <strong>{selectedSchool?.name}</strong>?</p>
                    <p className="mt-2 text-amber-600">All school administrators and users will be unable to login until the school is unsuspended.</p>
                  </>
                )}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSuspendDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                className={selectedSchool?.is_suspended ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-amber-600 hover:bg-amber-700'}
                onClick={handleSuspendToggle}
                disabled={formLoading}
                data-testid="confirm-suspend-btn"
              >
                {formLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  selectedSchool?.is_suspended ? 'Unsuspend School' : 'Suspend School'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AdminSchoolsPage;
